import React, { useState, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { Order } from '../../interfaces';
import './EditOrderPopup.css';

interface EmailReceiptProps {
    order: Order;
    onClose: () => void;
    onBack: () => void;
    receiptEmailSent: () => void;
    isOpen: boolean;
    splitInfo?: { seatId: string; splitIndex: number; guestName: string; roomNumber: string };
}

const formatDateForReceipt = (date: Date): string => {
    return new Date(date).toLocaleString('en-US', {
        weekday: 'short',
        month: 'long',
        day: '2-digit',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
    });
};

const EmailReceipt: React.FC<EmailReceiptProps> = ({ isOpen, order, onClose, onBack, receiptEmailSent, splitInfo }) => {
    const [emailContent, setEmailContent] = useState({ title: '', description: '', email: '' });
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [windowSize, setWindowSize] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });
    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setEmailContent((prevContent) => ({
            ...prevContent,
            [name]: value,
        }));
    };

  
    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.onresize = handleResize;

        return () => {
            window.onresize = null;
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                setWindowSize({
                    width: window.innerWidth,
                    height: window.innerHeight,
                });
            }
        };
    
        document.addEventListener("visibilitychange", handleVisibilityChange);
    
        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        const updateSize = () => {
            console.log("update size")
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };
    
        window.addEventListener("resize", updateSize);
        window.addEventListener("focus", updateSize);
        document.addEventListener("visibilitychange", updateSize);
    
        return () => {
            window.removeEventListener("resize", updateSize);
            window.removeEventListener("focus", updateSize);
            document.removeEventListener("visibilitychange", updateSize);
        };
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);
    const convertToBase64 = (file: File): Promise<string> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                const base64String = (reader.result as string).split(',')[1];
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
        });
    };
    const handleSend = async () => {
        setIsLoading(true);
        try {
            const mailFiles = await Promise.all(
                uploadedFiles.map(async (file) => {
                    const base64File = await convertToBase64(file);
                    return {
                        File: base64File,
                        ContentType: file.type,
                        Path: file.name,
                    };
                })
            );

            const response = await fetch(`${process.env.REACT_APP_API_URL}/Order/email-receipt`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order: order,
                    email: emailContent.email,
                    title: emailContent.title,
                    description: emailContent.description,
                    files: mailFiles,
                    formattedDate: formatDateForReceipt(new Date()),
                    ...(splitInfo ? {
                        seatId: splitInfo.seatId,
                        splitIndex: splitInfo.splitIndex,
                        guestName: splitInfo.guestName,
                        roomNumber: splitInfo.roomNumber,
                    } : {}),
                }),
                credentials: 'include',
            });

            if (response.ok) {
                setIsLoading(false);
                receiptEmailSent();
                setEmailContent({ title: '', description: '', email: '' });
                setUploadedFiles([]);
            } else {
                console.error('Error sending email receipt:', response.statusText);
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error during email send:', error);
            setIsLoading(false);
        }
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files) {
            setUploadedFiles((prevFiles) => [...prevFiles, ...Array.from(files)]);
        }
    };

    const handleDeleteFile = (index: number) => {
        setUploadedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    const styles = StyleSheet.create({
        drawer: {
            top: 0,
            right: 0,
            width: windowSize.width * 0.47,
            height: windowSize.height,
            backgroundColor: 'white',
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: '#D0D5DD',
            elevation: 5,
            zIndex: 1002,
            position: 'absolute',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'scroll'
        },
        openDrawer: {
            right: 0,
        },
        closedDrawer: {
            right: -windowSize.width * 0.75,
        },

    });


    return (
        <View style={[styles.drawer, isOpen ? styles.openDrawer : styles.closedDrawer]}>
            <div className='edit-order-popup'>
                <div className='d-flex px-3 pt-3 justify-content-between'>
                    <h5>Email Receipt</h5>
                    <button className='text-btn p-0 m-0 d-flex justify-content-center' onClick={onClose}>
                        <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M13 1L1 13M1 1L13 13" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                </div>
                <hr />
                <div className='px-3 pb-3 d-flex flex-column'>
                    <button className='text-btn gap-2 d-flex align-items-center' style={{ color: '#1643E0', fontSize: 14 }} onClick={onBack}>
                        <svg width="16" height="16" viewBox="0 0 27 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M25.1673 10.5H1.83398M1.83398 10.5L10.584 19.25M1.83398 10.5L10.584 1.75" stroke="#1643E0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        Back
                    </button>
                    <label className='mt-4'>Email Address</label>
                    <input type='text'
                        name="email"
                        value={emailContent.email}
                        onChange={handleChange}
                        placeholder='Enter recipient email address'
                    />
                    <label className='mt-4'>Title</label>
                    <input type='text'
                        name="title"
                        value={emailContent.title}
                        onChange={handleChange}
                        placeholder='Enter email title'
                    />
                    <label className='mt-4'>Description</label>
                    <textarea
                        name="description"
                        value={emailContent.description}
                        onChange={handleChange}
                        placeholder='Enter email content'
                    />
                    <label className='mt-4'>Attach a file</label>
                    <div>
                        <input
                            type="file"
                            id="fileInput"
                            style={{ display: 'none' }}
                            onChange={handleFileUpload}
                        />
                        <button className='text-btn px-0' style={{ color: '#1643E0', fontSize: 14 }} onClick={() => document.getElementById('fileInput')?.click()}>
                            Add a file, image or video
                        </button>
                        <div className='d-flex align-items-center'>
                            <small >
                                receipt-{order.orderNumber}.pdf
                            </small>
                        </div>
                    </div>
                    {uploadedFiles.map((file, index) => (
                        <div className='d-flex align-items-center'>
                            <small key={index}>
                                {file.name}
                            </small>
                            <button
                                className='text-btn'
                                onClick={() => handleDeleteFile(index)}
                                aria-label={`Delete ${file.name}`}>
                                <svg width="8" height="8" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M13 1L1 13M1 1L13 13" stroke="#98A2B3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    ))}
                    <div className='d-flex justify-content-end mt-3'>
                        <button className='blue-filled-btn' onClick={handleSend} style={{ minWidth: 67 }}>
                            {isLoading ?
                                <span className="spinner" />
                                :
                                <div>
                                    Send
                                </div>
                            }
                        </button>
                    </div>
                </div>
            </div>
        </View>
    );
};



export default EmailReceipt;

